import { useState, type ReactElement } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, LifeBuoy, MessageSquare, Paperclip, Plus, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PaginationControls } from "@/components/PaginationControls";
import { useMyTickets } from "@/hooks/use-tickets";
import { TicketStatus, type TicketListItem } from "@/lib/api/tickets";
import { TicketStatusBadge } from "@/components/tickets/ticket-status-badge";
import { CreateTicketDialog } from "@/components/tickets/create-ticket-dialog";

export const Route = createFileRoute("/_authenticated/support")({
  validateSearch: (search: Record<string, unknown>): { status?: string; page?: number } => ({
    status: typeof search.status === "string" ? search.status : undefined,
    page:
      typeof search.page === "number"
        ? search.page
        : typeof search.page === "string"
          ? parseInt(search.page, 10) || undefined
          : undefined,
  }),
  component: SupportPage,
});

const STATUS_FILTERS: Array<{ key: string; label: string; status?: TicketStatus }> = [
  { key: "all", label: "All" },
  { key: "open", label: "Open", status: TicketStatus.Open },
  { key: "in_progress", label: "In Progress", status: TicketStatus.InProgress },
  { key: "resolved", label: "Resolved", status: TicketStatus.Resolved },
  { key: "closed", label: "Closed", status: TicketStatus.Closed },
];

function SupportPage(): ReactElement {
  const { isStudent } = useAuth();

  if (!isStudent) {
    return (
      <AccessDenied message="The support portal is for students. Staff manage tickets from the Tickets inbox." />
    );
  }

  return <StudentSupportPortalContent />;
}

function StudentSupportPortalContent(): ReactElement {
  const { status: currentStatusFilter, page: currentPageParam } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const activeTab = currentStatusFilter ?? "all";
  const activeConfig =
    STATUS_FILTERS.find((filter) => filter.key === activeTab) ?? STATUS_FILTERS[0];
  const targetStatus = activeConfig.status;
  const pageNumber = currentPageParam && currentPageParam > 0 ? currentPageParam : 1;

  const {
    data: ticketsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useMyTickets({
    status: targetStatus,
    pageNumber,
    pageSize: 20,
  });

  function handleTabChange(tabKey: string) {
    void navigate({
      search: (previous) => ({
        ...previous,
        status: tabKey === "all" ? undefined : tabKey,
        page: undefined,
      }),
    });
  }

  function handlePageChange(nextPage: number) {
    void navigate({
      search: (previous) => ({
        ...previous,
        page: nextPage > 1 ? nextPage : undefined,
      }),
    });
  }

  const items = ticketsData?.items ?? [];

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-foreground">
            <LifeBuoy className="h-6 w-6 text-primary shrink-0" aria-hidden="true" />
            Support
          </h1>
          <p className="text-sm text-muted-foreground">
            Get help with your courses, track your queries, and communicate with instructors and
            staff.
          </p>
        </div>

        <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5 shrink-0 self-start">
          <Plus className="h-4 w-4" aria-hidden="true" />
          <span>New Ticket</span>
        </Button>
      </header>

      <div className="space-y-4">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid grid-cols-3 sm:grid-cols-5 h-auto w-full max-w-lg">
            {STATUS_FILTERS.map((filter) => (
              <TabsTrigger key={filter.key} value={filter.key} className="text-xs sm:text-sm">
                {filter.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {isError ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-12 text-center">
            <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">Could not load your support tickets.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="rounded-lg border p-4 space-y-3 bg-card">
                <div className="flex justify-between items-start gap-4">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-5 w-20 shrink-0" />
                </div>
                <Skeleton className="h-4 w-1/3" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={LifeBuoy}
            title={
              activeTab === "all"
                ? "No support tickets yet"
                : `No ${activeConfig.label.toLowerCase()} tickets`
            }
            description={
              activeTab === "all"
                ? "Have a question or run into an issue? Create a support ticket to get assistance from your instructors and staff."
                : `You do not have any tickets with status "${activeConfig.label}".`
            }
            action={
              activeTab === "all" ? (
                <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  <span>New Ticket</span>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-3">
            {items.map((ticket) => (
              <TicketCardRow key={ticket.id} ticket={ticket} />
            ))}

            {ticketsData && ticketsData.totalPages > 1 && (
              <PaginationControls
                pageNumber={ticketsData.pageNumber}
                pageSize={ticketsData.pageSize}
                totalPages={ticketsData.totalPages}
                totalCount={ticketsData.totalCount}
                hasNextPage={ticketsData.hasNextPage}
                hasPreviousPage={ticketsData.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={() => {}}
                isLoading={isFetching}
              />
            )}
          </div>
        )}
      </div>

      <CreateTicketDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} />
    </div>
  );
}

function TicketCardRow({ ticket }: { ticket: TicketListItem }): ReactElement {
  const lastActivityDate = new Date(ticket.lastActivityAt);
  const isValidDate = !Number.isNaN(lastActivityDate.getTime());
  const relativeUpdated = isValidDate
    ? formatDistanceToNow(lastActivityDate, { addSuffix: true })
    : "recently";

  const hasAttachment = Boolean(
    (ticket as unknown as { attachment?: unknown; hasAttachment?: boolean }).attachment ||
      (ticket as unknown as { hasAttachment?: boolean }).hasAttachment,
  );

  return (
    <Card className="transition-all hover:border-primary/50 hover:shadow-xs">
      <CardContent className="p-4 sm:p-5">
        <Link
          to="/support/$ticketId"
          params={{ ticketId: ticket.id }}
          className="group block space-y-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
              {ticket.subject}
            </h2>
            <div className="shrink-0">
              <TicketStatusBadge status={ticket.status} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">{ticket.topicTitle}</span>
            <span>•</span>
            <time dateTime={ticket.lastActivityAt} title={lastActivityDate.toLocaleString()}>
              Updated {relativeUpdated}
            </time>
            <span>•</span>
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" aria-hidden="true" />
              {ticket.messageCount} {ticket.messageCount === 1 ? "reply" : "replies"}
            </span>
            {hasAttachment && (
              <>
                <span>•</span>
                <span className="inline-flex items-center gap-1">
                  <Paperclip className="h-3 w-3" aria-hidden="true" />
                  Attachment
                </span>
              </>
            )}
          </div>
        </Link>
      </CardContent>
    </Card>
  );
}
